import { parseSurveyTeamScheduledSlots } from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import type { StrapiQuotationRow, StrapiSamplingScheduleRow } from '../../api/strapiCrm';
import type { StrapiUser } from '../../api/strapi';
import {
  isAdminSampling as isAdminSamplingUser,
  quotationUsesSampleGroups,
} from '../QuotationsPage/quotationWorkflow';

export { quotationUsesSampleGroups };

export function isAdminWorkOrder(user: StrapiUser | null | undefined): boolean {
  const code = user?.userTypeCode?.trim().toLowerCase();
  return code != null && (code === 'admin_workorder' || code === 'admin_workorder_pic');
}

/** Hanya `admin_workorder` (bukan legacy pic / marketing) yang boleh ubah work order. */
export function canEditWorkOrder(user: StrapiUser | null | undefined): boolean {
  return user?.userTypeCode?.trim().toLowerCase() === 'admin_workorder';
}

export function isAdminSampling(user: StrapiUser | null | undefined): boolean {
  return isAdminSamplingUser(user);
}

/** Superadmin user_type — may perform both penjadwalan admin steps without impersonation. */
export function isSuperadminType(user: StrapiUser | null | undefined): boolean {
  return user?.userTypeCode?.trim().toLowerCase() === 'superadmin';
}

/** Hanya admin sampling (dan superadmin) yang mengisi kendaraan pada form penjadwalan. */
export function canEditScheduleTransportasiNote(user: StrapiUser | null | undefined): boolean {
  return isAdminSampling(user) || isSuperadminType(user);
}

/** Boleh edit tim & tanggal di halaman detail penjadwalan. */
export function canEditSamplingScheduleDetail(user: StrapiUser | null | undefined): boolean {
  return isAdminWorkOrder(user) || isAdminSampling(user) || isSuperadminType(user);
}

export function samplingScheduleJobIssued(
  row: Pick<StrapiSamplingScheduleRow, 'work_order'> | null | undefined,
): boolean {
  return Boolean(String(row?.work_order?.job_number ?? '').trim());
}

export function samplingScheduleIsLocked(
  row: Pick<StrapiSamplingScheduleRow, 'progress_status' | 'work_order' | 'schedule_kind'>,
): boolean {
  const status = row.progress_status ?? '';
  if (status === 'cancelled') return true;
  // Point reschedule stays editable by admin sampling even when already linked to a WO.
  if ((row.schedule_kind ?? 'initial') === 'point_reschedule') return false;
  if (row.work_order?.documentId) return true;
  return status === 'work_order_created';
}

/**
 * Admin work order dan admin sampling tetap boleh menambah, mengubah, dan menghapus tanggal
 * setelah work order tersimpan dan setelah nomor job terbit.
 */
export function samplingScheduleDetailLocked(
  schedule: Pick<StrapiSamplingScheduleRow, 'progress_status' | 'work_order' | 'schedule_kind'>,
  user: StrapiUser | null | undefined,
): boolean {
  if ((schedule.progress_status ?? '') === 'cancelled') return true;
  if (!canEditSamplingScheduleDetail(user)) return true;
  if ((schedule.schedule_kind ?? 'initial') === 'point_reschedule' && (isAdminSampling(user) || isSuperadminType(user))) {
    return false;
  }
  if (isAdminSampling(user) || isSuperadminType(user) || isAdminWorkOrder(user)) return false;
  return samplingScheduleIsLocked(schedule);
}

/** Admin sampling / superadmin tetap assign petugas & tanggal setelah WO dibuat. */
export function canAssignSurveyTeamAfterWorkOrder(user: StrapiUser | null | undefined): boolean {
  return isAdminSampling(user) || isSuperadminType(user);
}

/** Tombol penjadwalan sampling di grid quotation (customer_approved, metode sampling). */
export function canShowViewSamplingSchedulesFromQuotation(
  row: StrapiQuotationRow,
  user: StrapiUser | null | undefined,
): boolean {
  if (!user) return false;
  if (row.progress_status !== 'customer_approved') return false;
  if (row.sample_pickup_method === 'diantar') return false;
  if (!isAdminWorkOrder(user) && !isAdminSampling(user) && !isSuperadminType(user)) return false;
  return true;
}

export type ScheduleWorkOrderReadinessInput = Pick<
  StrapiSamplingScheduleRow,
  | 'work_order'
  | 'admin_workorder_completed_at'
  | 'customer_agreed_start_date'
  | 'customer_agreed_end_date'
  | 'customer_agreed_dates'
  | 'schedule_kind'
> & {
  progress_status?: string | null;
};

/** Admin WO selesai: tanggal disepakati customer sudah tersimpan. */
export function scheduleHasCustomerAgreedDates(
  row: Pick<
    StrapiSamplingScheduleRow,
    'admin_workorder_completed_at' | 'customer_agreed_start_date' | 'customer_agreed_dates'
  >,
): boolean {
  if (String(row.admin_workorder_completed_at ?? '').trim()) return true;
  if (Array.isArray(row.customer_agreed_dates) && row.customer_agreed_dates.length > 0) {
    return true;
  }
  return Boolean(String(row.customer_agreed_start_date ?? '').trim());
}

/** Admin sampling selesai: sudah ada petugas dengan slot jadwal. */
export function surveyTeamsHaveAssignedStaff(
  teams: ReadonlyArray<DynamicEntityRow>,
): boolean {
  return teams.some((team) => parseSurveyTeamScheduledSlots(team.scheduled_date).length > 0);
}

/**
 * Buat work order dari penjadwalan yang sudah punya tanggal kesepakatan.
 * Tim survey bukan syarat WO — admin sampling mengisi petugas setelahnya.
 */
export function canCreateWorkOrderFromSchedule(
  row: ScheduleWorkOrderReadinessInput,
  user: StrapiUser | null | undefined,
): boolean {
  if (!user) return false;
  if (!isAdminWorkOrder(user) && !isSuperadminType(user)) return false;
  if (row.progress_status === 'cancelled' || row.progress_status === 'work_order_created') return false;
  if ((row.schedule_kind ?? 'initial') === 'point_reschedule') return false;
  if (row.work_order?.documentId) return false;
  if (!scheduleHasCustomerAgreedDates(row)) return false;
  return true;
}

/** Minimal 2 petugas dengan slot jadwal dan satu ketua. */
export function surveyTeamsReadyForAdminSamplingApproval(
  teams: ReadonlyArray<DynamicEntityRow>,
): boolean {
  const withSlots = teams.filter(
    (team) => parseSurveyTeamScheduledSlots(team.scheduled_date).length > 0,
  );
  if (withSlots.length < 2) return false;
  return teams.some((team) => Boolean(team.is_lead));
}

/**
 * Admin sampling / superadmin menyetujui tim setelah WO ada, agar nomor job terbit
 * dan petugas boleh mulai sampling.
 */
export function canApproveSurveyTeamForSampling(
  schedule: Pick<StrapiSamplingScheduleRow, 'progress_status' | 'work_order' | 'schedule_kind'>,
  teams: ReadonlyArray<DynamicEntityRow>,
  user: StrapiUser | null | undefined,
): boolean {
  if (!isAdminSampling(user) && !isSuperadminType(user)) return false;
  if ((schedule.schedule_kind ?? 'initial') === 'point_reschedule') return false;
  if (schedule.progress_status === 'cancelled') return false;
  if (!schedule.work_order?.documentId) return false;
  if (String(schedule.work_order.job_number ?? '').trim()) return false;
  return surveyTeamsReadyForAdminSamplingApproval(teams);
}

export function workOrderNewPathFromSchedule(
  quotationDocumentId: string,
  scheduleDocumentId: string,
): string {
  const q = encodeURIComponent(quotationDocumentId);
  const s = encodeURIComponent(scheduleDocumentId);
  return `/quotation/${q}/work-order/new?scheduleId=${s}`;
}
